import { useState, useRef, useEffect } from "react";
import { FaChevronDown, FaCheck } from "react-icons/fa";

interface CustomSelectProps {
  id: string;
  label: string;
  required?: boolean;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  error?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  id,
  label,
  required = false,
  value,
  options,
  onChange,
  error,
}) => {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative w-full mt-6" ref={wrapperRef}>
      {/* Floating Label */}
      <label
        htmlFor={id}
        className={`absolute left-0 transition-all pointer-events-none px-1
          ${
            focused || value
              ? "-top-2 text-sm text-red-600 bg-white"
              : "top-3 text-gray-400 text-base"
          }`}
      >
        {label} {required && <span className="text-red-600">*</span>}
      </label>

      {/* Dropdown box */}
      <div
        tabIndex={0}
        onClick={() => setOpen((prev) => !prev)}
        onFocus={() => setFocused(true)}
        className={`w-full border-b-2 py-3 px-3 cursor-pointer flex justify-between items-center
          ${error ? "border-red-600" : "border-gray-300"} 
          focus:border-red-600 bg-white`}
      >
        <span className={value ? "text-gray-900" : "text-transparent"}>
          {value || label}
        </span>

        <FaChevronDown
          className={`ml-2 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </div>

      {/* Options */}
      {open && (
        <ul className="absolute z-10 w-full bg-white border border-gray-300 mt-1 rounded shadow-md max-h-60 overflow-auto">
          {options.map((opt) => {
            const isSelected = opt === value;

            return (
              <li
                key={opt}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                  setFocused(false);
                }}
                className={`px-3 py-2 cursor-pointer flex items-center justify-between
                  ${
                    isSelected
                      ? "bg-red-100 text-red-700 font-medium"
                      : "hover:bg-red-50"
                  }`}
              >
                <span>{opt}</span>
                {isSelected && <FaCheck className="text-red-600 text-sm" />}
              </li>
            );
          })}
        </ul>
      )}

      {/* Error */}
      {error && <p className="text-red-600 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default CustomSelect;
